import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Trophy, Medal, Crown, User, LayoutGrid, List, Mic2 } from 'lucide-react';

function LeaderboardPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCurrentUser();
    fetchLeaderboard();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCurrentUser(data);
      }
    } catch (error) {
      // Not logged in
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/votes/leaderboard`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Failed to fetch leaderboard');
      }
      
      setLeaderboard(data);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 0:
        return <Crown className="h-6 w-6 text-yellow-500" />;
      case 1:
        return <Trophy className="h-6 w-6 text-gray-400" />;
      case 2:
        return <Medal className="h-6 w-6 text-amber-700" />;
      default:
        return null;
    }
  };

  // Check if current user is already an artist
  const isArtist = currentUser?.is_artist;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
        <p className="text-lg text-muted-foreground">Loading......</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-4">
          ← Back to Home
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div className="mb-4 md:mb-0">
            <h1 data-testid="leaderboard-title" className="text-4xl md:text-5xl font-bold mb-2 tracking-tight">
              Leaderboard
            </h1>
            <p className="text-muted-foreground">See who's leading the competition</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Register as Artist Button - Hidden for artists */}
            {!isArtist && (
              <Button
                onClick={() => navigate('/join-talent-show')}
                className="bg-secondary hover:brightness-110 text-black rounded-full"
                data-testid="register-artist-btn-header"
              >
                <Mic2 className="mr-2 h-4 w-4" />
                Register as Artist
              </Button>
            )}
            
            {/* View Toggle */}
            <div className="flex gap-2 bg-white/5 p-1 rounded-full">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-full"
              >
                <LayoutGrid className="h-4 w-4 mr-2" />
                Cards
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-full"
              >
                <List className="h-4 w-4 mr-2" />
                List
              </Button>
            </div>
          </div>
        </div>

        {leaderboard.length === 0 ? (
          <Card className="glass-panel p-12 text-center">
            <Trophy className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
            <p className="text-xl text-muted-foreground mb-6">No Artists registered yet</p>
            {!isArtist && (
              <Button
                onClick={() => navigate('/join-talent-show')}
                className="bg-secondary hover:brightness-110 text-black rounded-full"
                data-testid="register-artist-btn"
              >
                <Mic2 className="mr-2 h-5 w-5" />
                Register as Artist
              </Button>
            )}
          </Card>
        ) : viewMode === 'grid' ? (
          // Grid/Card View
          <div className="space-y-4">
            {leaderboard.map((entry, index) => (
              <Card
                key={entry.artist_id}
                className={`glass-panel p-6 hover:border-primary/50 transition-all ${
                  index === 0 ? 'border-2 border-yellow-500/50 shadow-[0_0_30px_rgba(234,179,8,0.3)]' : ''
                }`}
                data-testid={`leaderboard-entry-${index}`}
              >
                <div className="flex items-center gap-6">
                  <div className="flex items-center justify-center w-16">
                    {index < 3 ? (
                      getRankIcon(index)
                    ) : (
                      <div className="text-3xl font-bold text-muted-foreground">
                        #{index + 1}
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{entry.artist_name || `Artist ${entry.artist_id.slice(-6)}`}</h3>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Public Votes</p>
                        <p className="text-xl font-bold text-secondary">{entry.public_votes}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Judge Votes</p>
                        <p className="text-xl font-bold text-primary">{entry.judge_votes}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total</p>
                        <p className="text-2xl font-bold text-accent">{entry.total_votes}</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => navigate(`/profile/${entry.artist_id}`)}
                    className="rounded-full"
                  >
                    <User className="mr-2 h-4 w-4" />
                    View Profile
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          // List/Table View
          <Card className="glass-panel overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="text-left p-4 w-16">#</th>
                  <th className="text-left p-4">Artist</th>
                  <th className="text-center p-4">Public</th>
                  <th className="text-center p-4">Judge</th>
                  <th className="text-center p-4">Total</th>
                  <th className="text-center p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.map((entry, index) => (
                  <tr 
                    key={entry.artist_id} 
                    className={`border-b border-white/10 hover:bg-white/5 ${index < 3 ? 'bg-yellow-500/5' : ''}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center justify-center">
                        {index < 3 ? getRankIcon(index) : <span className="text-muted-foreground">#{index + 1}</span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center overflow-hidden">
                          {entry.artist_picture ? (
                            <img src={entry.artist_picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-4 w-4 text-primary" />
                          )}
                        </div>
                        <span className="font-medium">{entry.artist_name || `Artist ${entry.artist_id.slice(-6)}`}</span>
                      </div>
                    </td>
                    <td className="text-center p-4 font-bold text-secondary">{entry.public_votes}</td>
                    <td className="text-center p-4 font-bold text-primary">{entry.judge_votes}</td>
                    <td className="text-center p-4 font-bold text-accent text-lg">{entry.total_votes}</td>
                    <td className="text-center p-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/profile/${entry.artist_id}`)}
                        className="rounded-full"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}

export default LeaderboardPage;
